import React, { useState, useRef, FunctionComponent } from "react";
import { AppBar, Toolbar, Box, Button, Paper, MenuItem, Typography, Divider } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

interface DropdownItem {
  title: string;
  link: string;
  submenu?: DropdownItem[];
}

export const navItems = [
  "Products",
  "Markets",
  "Distributors",
  "Catalogs",
  "The Company",
  "Contact Us",
  "FAQs",
];

interface FlagProps {
  country: string;
  flagUrl: string;
}

export const dropdownItems: Record<string, DropdownItem[]> = {
  Products: [
    {title: "View all Products", link: "https://www.barksdale.com/tools-resources/reference-library", submenu: []},
    {title: "Product List", link: "", submenu: [
      { title: "Pressure Switches", link: "https://www.barksdale.com/products/pressure/mechanical-pressure-switches"},
      { title: "Temperature Switches", link: "https://www.barksdale.com/products/temperature" },
      { title: "Transducers/Transmitters", link: "https://www.barksdale.com/products/pressure/pressure-transducer" },
      { title: "Level Control", link: "https://www.barksdale.com/products/pressure/pressure-transducer" },
      { title: "Valves and Regulators", link: "https://www.barksdale.com/products/valves" },
      { title: "Electronic Switches", link: "https://www.barksdale.com/products/pressure/electronic-switches" },
      { title: "Air Suspension Valves", link: "https://www.barksdale.com/products/air-suspension-valves" },
    ]},
    // {title: "RapidNet ® - 24 hr. Shipment", link: "https://www.barksdale.com/tools-resources/reference-library", submenu: []},
    {title: "Product Selector", link: "", submenu: [
      { title: "Pressure Switches", link: "http://www2.barksdale.com/prodselect/pressure.asp" },
      { title: "Temperature Switches", link: "http://www2.barksdale.com/prodselect/tempswitch.asp" },
      { title: "Transducers/Transmitters", link: "http://www2.barksdale.com/prodselect/transducer.asp" },
      { title: "Level Control", link: "http://www2.barksdale.com/prodselect/levelsite.asp" },
    ]},
    { title: "Competitive Cross Reference", link: "https://apps.barksdale.com/crossref/searchpartnumber.asp", submenu: []},
  ],
  Markets: [
    { title: "Construction & Mining", link: "https://www.barksdale.com/industries/industrial", submenu: []},
    { title: "Marine", link: "https://www.barksdale.com/industries/industrial", submenu: []},
    { title: "Oil & Gas", link: "https://www.barksdale.com/industries/oil-gas", submenu: []},
    { title: "Factory Automation", link: "https://www.barksdale.com/industries/industrial", submenu: []},
    { title: "Transportation", link: "https://www.barksdale.com/industries/transportation", submenu: []}
  ],
  Distributors: [
    {title: "North America", link: "https://www.barksdale.com/tools-resources/find-distributor", submenu: []},
    {title: "World", link: "https://www.barksdale.com/en/distributors/world/", submenu: []},
  ],
  Catalogs: [
    {title: "Download Literature", link: "https://www.barksdale.com/tools-resources/reference-library", submenu: []},
  ],
  "The Company": [
    {title: "About Us", link: "https://www.barksdale.com/about-us", submenu: []},
  ],
  "Contact Us": [
    {title: "Contact Info", link: "https://www.barksdale.com/tools-resources/find-distributor", submenu: []},
    {title: "Technical Request", link: "https://www.barksdale.com/tools-resources/find-distributor", submenu: []},
    {title: "Quote Request", link: "https://www.barksdale.com/tools-resources/find-distributor", submenu: []},
  ],
  FAQs: [
    { title: "Pressure Switch", link: 'https://www.barksdale.com/tools-resources/faqarticles', submenu: []},
    { title: "Temperature Switch", link: 'https://www.barksdale.com/tools-resources/faqarticles', submenu: []},
    { title: "Transducer", link: 'https://www.barksdale.com/en/FAQ/FAQ-transducer/', submenu: []},
    { title: "Solid State", link: 'https://www.barksdale.com/en/FAQ/FAQ-electronic/', submenu: []},
    { title: "Level Site ®", link: 'https://www.barksdale.com/en/FAQ/FAQ-LevelSite/', submenu: []},
  ],
};

interface TopNavProps {
  showFlag?: boolean;
}

const TopNav: FunctionComponent<TopNavProps> = ({showFlag=false}) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLButtonElement>, item: string) => {
    clearTimeout(timeoutRef.current!);
    setHoveredItem(item);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = window.setTimeout(() => {
      setHoveredItem(null);
    }, 300);
  };

  const cancelClose = () => {
    clearTimeout(timeoutRef.current!);
  };

  const FlagButton = ({ country, flagUrl }: FlagProps) => (
    <Button
      variant="contained"
      sx={{
        backgroundColor: '#C8102E',
        color: 'white',
        fontWeight: 500,
        textTransform: 'none',
        px: 2,
        borderRadius: '0px',
        '&:hover': {
          backgroundColor: '#a50d24',
        },
      }}
      startIcon={
        <img src={flagUrl} alt={`${country} flag`} width="20" style={{ border: '1px solid white' }} />
      }
    >
      {country}
    </Button>
  );

  return (
    <Box
      sx={{ position: "relative" }}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={cancelClose}
    >
      <AppBar
        position="static"
        sx={{ backgroundColor: "#000", display: "flex", alignItems: "center" }}
      >
        <Toolbar sx={{ gap: 3, minHeight: showFlag ? "50px !important" : "40px !important", justifyContent: "space-between", width: '100%' }}>
          {
            showFlag && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FlagButton
                  country="China"
                  flagUrl="https://flagcdn.com/w40/cn.png"
                />
                <FlagButton
                  country="Europe"
                  flagUrl="https://upload.wikimedia.org/wikipedia/commons/b/b7/Flag_of_Europe.svg"
                />
              </Box>
            )
          }
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {navItems.map((item) => (
                <Button
                  key={item}
                  color="inherit"
                  onMouseEnter={(e) => handleMouseEnter(e, item)}
                  endIcon={<ArrowDropDownIcon sx={{ color: "#d01e42" }} />}
                >
                  {item}
                </Button>
              ))}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {hoveredItem && dropdownItems[hoveredItem] && (
        <Paper
          sx={{
            display: 'flex',
            justifyContent: 'space-evenly',
            position: "fixed",
            backgroundColor: "#fff",
            zIndex: 9999,
            p: 2,
            boxShadow: 4,
            gap: 3,
            width: '100%',
            opacity: 0.9
          }}
        >
          {dropdownItems[hoveredItem].map((group) => (
            <Box key={group.title}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: "bold", cursor: group?.submenu?.length === 0 ? 'pointer': 'default' }}
                onClick={() => {
                  if (group?.submenu?.length === 0) {
                    window.open(group.link, "_self");
                    setHoveredItem(null);
                  }
                }}
              >
                {group.title}
                <Divider sx={{borderBottom: "2px solid #c8102e", width: '35px' }} />
              </Typography>

              {group.submenu ? (
                group.submenu.map((subItem) => (
                  <MenuItem
                    key={subItem.title}
                    onClick={() => {
                      window.open(subItem.link, "_self");
                      setHoveredItem(null);
                    }}
                    sx={{ pl: 0, fontSize: '14px' }}
                  >
                    <ArrowRightIcon fontSize="small" sx={{ marginLeft: '-7px', color: '#c8102e' }} /> {subItem.title}
                  </MenuItem>
                ))
              ) : (
                <MenuItem
                  onClick={() => {
                    window.open(group.link, "_self");
                    setHoveredItem(null);
                  }}
                  sx={{ pl: 0 }}
                >
                  {group.title}
                </MenuItem>
              )}
            </Box>
          ))}
        </Paper>
      )}

    </Box>
  );
};

export default TopNav;
